import { formatIDR } from "~/lib/format";

/**
 * The conversions between what an operator types and what the fee schedule
 * endpoints take (PRO-1200).
 *
 * The API speaks basis points and decimal strings so a rate never passes
 * through a float on the server. Operators speak "0.7%" and "Rp 4.000". These
 * helpers are the only place the two meet, and they refuse input they would
 * have to round: a rate that silently became something else is the bug a fee
 * screen must not have.
 */

/** 70 -> "0.70%". */
export function formatBps(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}

/** "0.7" or "0.70%" -> 70. Null for anything that is not a percentage with
 *  at most two decimals between 0 and 100. */
export function parsePercentToBps(raw: string): number | null {
  const cleaned = raw.trim().replace(/%$/, "").replace(",", ".").trim();
  if (cleaned === "") return 0;
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(cleaned)) return null;
  const [whole, fraction = ""] = cleaned.split(".");
  const bps = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  return bps <= 10000 ? bps : null;
}

/** "4000", "4.000" or "4000.00" -> "4000.00". Null for anything else. Dots are
 *  read as thousands separators unless followed by exactly two digits at the
 *  end, which is the only decimal shape the API emits. */
export function parseRupiah(raw: string): string | null {
  let cleaned = raw.trim().replace(/^rp\s*/i, "").replace(/\s/g, "");
  if (cleaned === "") return "0.00";
  let fraction = "00";
  const decimal = /^(.*)[.,](\d{2})$/.exec(cleaned);
  if (decimal && !/^\d{1,3}(\.\d{3})+$/.test(cleaned)) {
    cleaned = decimal[1] ?? "";
    fraction = decimal[2] ?? "00";
  }
  cleaned = cleaned.replace(/\./g, "");
  if (!/^\d+$/.test(cleaned)) return null;
  // Leading zeros stripped on the string, not through Number(): a rupiah
  // amount past 2^53 minor units is absurd, but this must not be the place
  // that rounds it.
  return `${cleaned.replace(/^0+(?=\d)/, "")}.${fraction}`;
}

/** A schedule's rate as one phrase: "0.70%", "Rp 4.000", or both. */
export function describeRate(bps: number, fixed: string): string {
  const parts: string[] = [];
  if (bps > 0) parts.push(formatBps(bps));
  if (!/^0+(\.0+)?$/.test(fixed)) parts.push(formatIDR(fixed));
  return parts.length ? parts.join(" + ") : "free";
}

/**
 * A date input's YYYY-MM-DD as midnight in Jakarta.
 *
 * Settlement dates arrive in UTC, but "from 1 September" means the start of
 * the Indonesian day to everyone typing it, and a UTC midnight would leave the
 * first seven hours of that day priced by the previous window.
 */
export function wibMidnight(date: string): string {
  return `${date}T00:00:00+07:00`;
}

/** Today in Jakarta as YYYY-MM-DD, for date input defaults. */
export function todayInJakarta(offsetDays = 0): string {
  const now = new Date(Date.now() + offsetDays * 86_400_000);
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta" }).format(now);
}

/** An instant as its Jakarta calendar date, YYYY-MM-DD. */
export function jakartaDate(iso: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta" }).format(new Date(iso));
}
